import { FC, ReactNode, useState, useCallback, useEffect } from 'react';
import { graphql, useFragment } from 'react-relay';
import { filterFalsy } from 'dibs-ts-utils/exports/filterFalsy';
import type {
    PaymentMethod,
    PaymentMethodOptionInput,
    Address,
    ChargeCurrency,
    PaymentMethodType,
} from '../types';
import { isStripeElementPaymentMethod, parsePaymentMethodOptionInput } from '../types';
import {
    PaymentMethodSelectContext,
    type PaymentMethodConfigOptions,
    type CreditCardForm,
    type AchForm,
    AchFormErrors,
} from './PaymentMethodSelectContext';
import { PaymentMethodSelectStripeElements } from './PaymentMethodSelectStripeElements';
import { useStripePromise } from './stripe/useStripePromise';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';

import { PaymentMethodSelect_viewer$key } from './__generated__/PaymentMethodSelect_viewer.graphql';
import { defaultFormValue } from 'dibs-ach-form/exports/ACHForm';

const viewerFragment = graphql`
    fragment PaymentMethodSelect_viewer on Viewer {
        id
        clientToken {
            value
        }
    }
`;

type Props = {
    viewer: PaymentMethodSelect_viewer$key;
    paymentMethods: PaymentMethodOptionInput[];
    initialPaymentMethod?: PaymentMethod | null;
    chargeCurrency: ChargeCurrency;
    userId?: string | null;
    shippingAddressId?: string;
    billingAddress?: Address;
    onChange?: (paymentMethod: PaymentMethod) => void;
    children: ReactNode;
    paymentMethodOptions?: PaymentMethodConfigOptions;
    mockWallet?: boolean;
    hideWallet?: boolean;
    paymentMethodOrder?: PaymentMethodType[];
};

export const PaymentMethodSelect: FC<Props> = props => {
    const {
        viewer: viewerRef,
        paymentMethods: paymentMethodsInput,
        chargeCurrency,
        initialPaymentMethod,
        userId,
        billingAddress,
        onChange,
        children,
        paymentMethodOptions,
        mockWallet,
        hideWallet,
        shippingAddressId,
        paymentMethodOrder = [],
    } = props;

    const stripe = useStripePromise(chargeCurrency);

    const viewer = useFragment(viewerFragment, viewerRef);
    const braintreeClientToken = viewer?.clientToken?.value || '';
    const viewerId = viewer?.id || '';

    const orderedPaymentMethodInput = paymentMethodOrder.map(paymentMethodType =>
        paymentMethodsInput.find(option => option.paymentMethodType === paymentMethodType)
    );
    const unorderedPaymentMethodInput = paymentMethodsInput.filter(
        option =>
            !orderedPaymentMethodInput.some(
                orderedOption => orderedOption?.paymentMethodType === option.paymentMethodType
            )
    );
    const paymentMethods = [...orderedPaymentMethodInput, ...unorderedPaymentMethodInput]
        .map(option => {
            if (!option) {
                return null;
            }
            const paymentMethod = parsePaymentMethodOptionInput(option);
            if (!paymentMethod) {
                // eslint-disable-next-line
                console.warn(
                    `PaymentMethodSelect: unkown payment method with type ${option?.paymentMethodType} and processor ${option?.paymentProcessor}, skipping`
                );
            }
            return paymentMethod;
        })
        .filter(filterNulls);

    const [isBusy, setIsBusy] = useState(false);

    const [selectedPaymentMethod, setSelectedPaymentMethod] = useState<PaymentMethod | null>(null);

    // initial payment method might be set after the selector is rendered, so until there is a selected payment method,
    // we're "listening" to changes in the initialPaymentMethod prop
    useEffect(() => {
        if (selectedPaymentMethod) {
            return;
        }

        const initialPaymentMethodType = initialPaymentMethod?.paymentMethodType;
        const initialPaymentMethodToken = initialPaymentMethod?.paymentMethodToken;
        const initialPaymentMethodProcessor = initialPaymentMethod?.paymentProcessor;

        if (!initialPaymentMethodType || !initialPaymentMethodProcessor) {
            return;
        }

        const initialPaymentMethodValid = paymentMethods.some(method => {
            return (
                method.paymentMethodType === initialPaymentMethodType &&
                method.paymentProcessor === initialPaymentMethodProcessor
            );
        });

        if (initialPaymentMethodValid) {
            setSelectedPaymentMethod({
                paymentMethodType: initialPaymentMethodType,
                paymentProcessor: initialPaymentMethodProcessor,
                paymentMethodToken: initialPaymentMethodToken,
            });
        }
    }, [selectedPaymentMethod, paymentMethods, initialPaymentMethod]);

    const updateSelectedPaymentMethod = useCallback(
        (paymentMethod: PaymentMethod) => {
            setSelectedPaymentMethod(paymentMethod);

            if (onChange) {
                onChange(paymentMethod);
            }
        },
        [onChange]
    );

    const [creditCardForm, setCreditCardForm] = useState<CreditCardForm>(() => {
        const cardHolderName = paymentMethodOptions?.CREDIT_CARD?.cardHolderName || '';
        return { cardHolderName };
    });

    const updateCreditCardForm = useCallback((formUpdate: Partial<CreditCardForm>) => {
        setCreditCardForm(currentForm => ({
            ...currentForm,
            ...formUpdate,
        }));
    }, []);

    const [creditCardFormErrors, setCreditCardFormErrors] = useState<CreditCardForm>({});

    const [achForm, setAchForm] = useState<AchForm>(defaultFormValue);

    const updateAchForm = useCallback((formUpdate: Partial<AchForm>) => {
        setAchForm(currentForm => ({
            ...currentForm,
            ...formUpdate,
        }));
    }, []);

    const [achFormErrors, setAchFormErrors] = useState<AchFormErrors>({});

    const stripePaymentMethods = paymentMethods
        .filter(isStripeElementPaymentMethod)
        .map(paymentMethod => paymentMethod.processorPaymentMethodName)
        .filter(filterFalsy);
    const hasStripePaymentMethods = !!stripePaymentMethods.length;
    const isStripeReady = !hasStripePaymentMethods || (hasStripePaymentMethods && !!stripe);

    if (!isStripeReady) {
        return null;
    }

    const contextValue = {
        paymentMethods,
        stripePaymentMethods,
        chargeCurrency,
        selectedPaymentMethod,
        userId,
        billingAddress,
        updateSelectedPaymentMethod,
        paymentMethodOptions,
        shippingAddressId,
        isBusy,
        setIsBusy,
        mockWallet: !!mockWallet,
        hideWallet: !!hideWallet,
        braintreeClientToken,
        viewerId,
        creditCardForm,
        creditCardFormErrors,
        updateCreditCardForm,
        updateCreditCardFormErrors: setCreditCardFormErrors,
        achForm,
        achFormErrors,
        updateAchForm,
        updateAchFormErrors: setAchFormErrors,
    };

    return (
        <PaymentMethodSelectStripeElements stripe={stripe} paymentMethods={stripePaymentMethods}>
            <PaymentMethodSelectContext.Provider value={contextValue}>
                {children}
            </PaymentMethodSelectContext.Provider>
        </PaymentMethodSelectStripeElements>
    );
};
